import { useEffect, useState } from "react";
import style from "./SignUpForm.module.scss";
import { Link } from "react-router-dom";
import TextInput, { PasswordInput } from "../../../../components/TextInput";
import CustomButton from "../../../../components/CustomButton";
import InfoCircleIcon from "@mui/icons-material/InfoRounded";
import { Alert } from "@mui/material";
import reCAPTCHA from "../../../../utils/recaptcha";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { strongPasswordRegex } from "../../../../utils/constants";
import { userSlice } from "../../../../store/reducer/user";
import { authSlice } from "../../../../store/reducer/auth";

const SignUpForm = () => {
  const dispatch = useDispatch();
  const [recaptcha, setRecaptcha] = useState<reCAPTCHA | null>(null);
  const [recaptchaInitError, setRecaptchaInitError] = useState<string | null>(
    null
  );

  const username = useSelector((state: RootState) => state.user.data?.username);
  const errors = useSelector((state: RootState) => state.auth.formErrors);
  const { 
    password, 
    password_confirmation 
  } = useSelector((state: RootState) => state.auth.passwordValues);

  useEffect(() => {
    let recaptchaInitialized = false;
    if (!recaptchaInitialized) {
      setRecaptcha(
        new reCAPTCHA(import.meta.env.VITE_APP_RECAPTCHA_SITEKEY!, "login")
      );
    }

    return () => {
      recaptchaInitialized = true;
    };
  }, []);

  const doRegister = async () => {
    if (recaptcha !== null) {
      const captchaToken = await recaptcha.getToken();
      dispatch(authSlice.actions.resetFormErrors());
      dispatch(authSlice.actions.resetError());
      dispatch(authSlice.actions.signup({
        password,
        password_confirmation,
        captcha_value: captchaToken
      }));
    } else {
      setRecaptchaInitError(
        "Could not initialize reCaptcha. Please reload page and try again."
      );
    }
  };

  return (
    <ValidatorForm
      className={'auth-container'}
      onSubmit={doRegister}
      onError={(errors) => console.log(errors)}
    >
      {(errors.email ||
        errors.social_security ||
        errors.captcha_value ||
        recaptchaInitError !== null) && (
          <Alert
            severity="error"
            variant="outlined"
            iconMapping={{
              error: <InfoCircleIcon />,
            }}
          >
            {!!errors.email && <div>{errors.email}</div>}
            {!!errors.social_security && <div>{errors.social_security}</div>}
            {!!errors.captcha_value && <div>{"Captcha validation failed"}</div>}
            {recaptchaInitError !== null && <div>{recaptchaInitError}</div>}
          </Alert>
        )}
      <TextInput
        label="Username"
        placeholder="Username"
        className={style.textField}
        value={username}
        name="username"
        onChange={(e) => {
          dispatch(userSlice.actions.updateUserData({ username: e.target.value }));
        }}
        error={errors.username?.length ? true : false}
        helperText={errors.username}
        autoComplete="off"
        inputProps={{ tabIndex: 0 }}
        validators={["required", "minStringLength:7"]}
        errorMessages={["This field is required", "Username must be at least 7 characters"]}
      />
      <PasswordInput
        label="Password"
        placeholder="Password"
        className={style.textField}
        value={password}
        name="password"
        onChange={(e) => {
          dispatch(authSlice.actions.updatePasswordValues({ 
            password: e.target.value 
          }));
        }}
        /*onFocus={() => {
          passwordRequirementsRef.current?.classList.add(style.show);
        }}
        onBlur={() => {
          passwordRequirementsRef.current?.classList.remove(style.show);
        }}*/
        error={errors.password?.length ? true : false}
        helperText={errors.password}
        inputProps={{ tabIndex: 0 }}
        validators={[
          "required",
          `matchRegexp:${strongPasswordRegex.source}`,
          "passwordMatches:auth.passwordValues.password_confirmation"
        ]}
        errorMessages={[
          "This field is required",
          "Password must be 8 characters and contain at least one uppercase, one lowercase, one symbol, and one number.",
          "Passwords do not match"
        ]}
      />
      <PasswordInput
        label="Re-type password"
        placeholder="Re-type password"
        className={style.textField}
        value={password_confirmation}
        name="password_confirmation"
        onChange={(e) => {
          dispatch(authSlice.actions.updatePasswordValues({ 
            password_confirmation: e.target.value 
          }));
        }}
        error={errors.password_confirmation?.length ? true : false}
        helperText={errors.password_confirmation}
        inputProps={{ tabIndex: 0 }}
        validators={[
          "required",
          "passwordMatches:auth.passwordValues.password"
        ]}
        errorMessages={[
          "This field is required",
          "Passwords do not match"
        ]}
      />
      <Link to="/" className={style.frgtLink}>
        I already have an account.
      </Link>
      <CustomButton
        variant="contained"
        disableElevation={true}
        tabIndex={0}
        label={"create new account"}
        type={"submit"}
      />
      <div id="recaptcha-container"></div>
    </ValidatorForm>
  );
};

export default SignUpForm;
